import type { Direction } from './types';

const SWIPE_MIN_PX    = 30;   // minimum distance to register as a swipe
const SWIPE_MAX_MS    = 400;  // ignore slow drags longer than this
const AXIS_LOCK_RATIO = 2.0;  // one axis must be this many times stronger to lock direction

// Attach swipe (touch) and arrow-key listeners to the canvas.
// Returns a cleanup function to remove all listeners.
export function attachSwipeListener(
  element: HTMLCanvasElement,
  onSwipe: (dir: Direction) => void,
): () => void {
  let startX    = 0;
  let startY    = 0;
  let startTime = 0;

  function onTouchStart(e: TouchEvent) {
    e.preventDefault();
    const t  = e.changedTouches[0];
    startX    = t.clientX;
    startY    = t.clientY;
    startTime = performance.now();
  }

  function onTouchEnd(e: TouchEvent) {
    e.preventDefault();
    const t    = e.changedTouches[0];
    const dx   = t.clientX - startX;
    const dy   = t.clientY - startY;
    const dt   = performance.now() - startTime;
    const dist = Math.hypot(dx, dy);

    if (dist < SWIPE_MIN_PX || dt > SWIPE_MAX_MS) return;

    const dir = detectDirection(dx, dy);
    if (dir) onSwipe(dir);
  }

  function onKey(e: KeyboardEvent) {
    const map: Record<string, Direction> = {
      ArrowLeft:  'left',
      ArrowRight: 'right',
      ArrowUp:    'up',
      ArrowDown:  'down',
    };
    const dir = map[e.key];
    if (dir) {
      e.preventDefault();
      onSwipe(dir);
    }
  }

  element.addEventListener('touchstart', onTouchStart, { passive: false });
  element.addEventListener('touchend',   onTouchEnd,   { passive: false });
  window.addEventListener('keydown', onKey);

  return () => {
    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchend',   onTouchEnd);
    window.removeEventListener('keydown', onKey);
  };
}

function detectDirection(dx: number, dy: number): Direction {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  if (adx > ady * AXIS_LOCK_RATIO) return dx > 0 ? 'right' : 'left';
  if (ady > adx * AXIS_LOCK_RATIO) return dy > 0 ? 'down'  : 'up';

  // Diagonal — use whichever axis dominates
  return adx >= ady
    ? (dx > 0 ? 'right' : 'left')
    : (dy > 0 ? 'down'  : 'up');
}
